import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface ColumnItem {
  id: string;
  date: string;
  title: string;
  content: string;
  author: string;
  category: string;
  audience: string;
  createdAt: number;
}

const ColumnSection = () => {
  const [columnList, setColumnList] = useState<ColumnItem[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem('heartFailureColumns');
    if (stored) {
      const parsed = JSON.parse(stored);
      const sorted = parsed.sort((a: ColumnItem, b: ColumnItem) => b.createdAt - a.createdAt);
      setColumnList(sorted.slice(0, 3));
    } else {
      // サンプルコラムを作成
      const sampleColumns: ColumnItem[] = [
        {
          id: 'sample-1',
          date: '2025年1月15日',
          title: '心臓リハビリテーションで大切な運動強度の設定について',
          content: '心臓リハビリテーションでは、適切な運動強度の設定が非常に重要です。心肺運動負荷試験（CPX）を用いて、個々の患者さんに最適な運動強度を決定します。嫌気性代謝閾値（AT）レベルでの運動は、心臓に過度な負担をかけずに効果的なトレーニングが可能です。\n\n運動強度が低すぎると十分な効果が得られず、高すぎると心臓に負担がかかり危険です。当院では、CPXの結果に基づいて個別の運動処方箋を作成し、安全で効果的なリハビリテーションを提供しています。\n\n退院後も継続的な運動が重要ですので、地域の医療機関と連携しながら、患者さんの運動習慣をサポートしています。',
          author: '理学療法士',
          category: '運動指導',
          audience: '患者さんへ',
          createdAt: Date.now() - 86400000
        },
        {
          id: 'sample-2',
          date: '2025年1月10日',
          title: '心不全患者さんの栄養管理のポイント',
          content: '心不全の患者さんにとって、適切な栄養管理は症状の改善と再発予防に欠かせません。特に重要なのは、塩分制限と水分管理です。\n\n塩分は1日6g未満を目標とし、加工食品や外食には注意が必要です。また、体重の急激な増加は体液貯留のサインですので、毎日同じ時間に体重を測定することをお勧めします。\n\nタンパク質やエネルギーの適切な摂取も重要で、低栄養状態は心不全の予後を悪化させます。当院では管理栄養士が個別の栄養指導を行い、患者さん一人ひとりに合わせた食事プランを提案しています。',
          author: '管理栄養士',
          category: '栄養指導',
          audience: '患者さんへ',
          createdAt: Date.now() - 172800000
        }
      ];
      localStorage.setItem('heartFailureColumns', JSON.stringify(sampleColumns));
      setColumnList(sampleColumns);
    }
  }, []);

  if (columnList.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="text-gray-500 text-sm font-medium mb-3 uppercase tracking-wider">Column</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900">コラム</h2>
          </div>
          <Link
            to="/column"
            className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium text-sm cursor-pointer transition-colors"
          >
            すべて見る
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="space-y-4">
          {columnList.map((column) => (
            <Link
              key={column.id}
              to="/column"
              className="block bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 group cursor-pointer"
            >
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <span className="inline-block px-4 py-2 bg-primary-100 text-primary-700 text-sm font-medium rounded-full whitespace-nowrap">
                    {column.date}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {column.audience}
                    </span>
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {column.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {column.title}
                  </h3>
                </div>
                <div className="flex-shrink-0">
                  <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-all duration-300"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ColumnSection;
